/**
 * useCurrentRep — resolves the currently-acting sales rep.
 *
 * Frontend-only prototype: no auth yet. This hook returns the canonical
 * default rep (`SALES_REP_ROSTER[0]` — Wesley Woodfin) and supports a
 * `localStorage` override (`koredor.currentRep`) for demo-mode rep
 * switching. Mirrors the storage pattern used by UISkin / NavMode.
 *
 * When auth is wired later, swap the implementation here without
 * touching consumers.
 */
import { useCallback, useEffect, useState } from "react";
import {
  SALES_REP_ROSTER,
  type SalesRepProfile,
  isCanonicalRep,
  getRepByName,
} from "@/data/rosters/salesReps";
import { useCurrentUser } from "@/hooks/useCurrentUser";

const STORAGE_KEY = "koredor.currentRep";

function readInitial(): SalesRepProfile {
  if (typeof window === "undefined") return SALES_REP_ROSTER[0];
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isCanonicalRep(stored)) {
      const found = getRepByName(stored);
      if (found) return found;
    }
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return SALES_REP_ROSTER[0];
}

export interface UseCurrentRepResult {
  rep: SalesRepProfile;
  /** Convenience — `rep.name`. Stable string for selectors. */
  repName: string;
  /** Switch the active rep. No-op if name is not in the canonical roster. */
  setRep: (name: string) => void;
}

export function useCurrentRep(): UseCurrentRepResult {
  const { profile } = useCurrentUser();
  const [rep, setRepState] = useState<SalesRepProfile>(readInitial);

  // Signed-in account is authoritative. If a profile name exists, use it
  // even when it is outside the canonical sales roster.
  useEffect(() => {
    const profileName = String(profile?.name ?? "").trim();
    if (!profileName) return;
    const canonical = getRepByName(profileName);
    if (canonical) {
      setRepState(canonical);
      return;
    }
    setRepState({
      id: String(profile?.id ?? `auth-${profileName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`),
      name: profileName,
      region: "Assigned",
      homeBase: "",
      coverageZips: [],
    });
  }, [profile?.id, profile?.name]);

  // Cross-tab sync
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && isCanonicalRep(e.newValue)) {
        const next = getRepByName(e.newValue);
        if (next) setRepState(next);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setRep = useCallback((name: string) => {
    // When signed in, auth profile controls the active identity.
    if (profile?.name) return;
    if (!isCanonicalRep(name)) return;
    const next = getRepByName(name);
    if (!next) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, name);
    } catch {
      /* ignore */
    }
    setRepState(next);
  }, [profile?.name]);

  return { rep, repName: rep.name, setRep };
}

/**
 * Module-level accessor for non-React contexts (selectors, helpers).
 * Reads localStorage once at call time — does not subscribe to changes.
 */
export function getCurrentRepName(): string {
  if (typeof window === "undefined") return SALES_REP_ROSTER[0].name;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && isCanonicalRep(stored)) return stored;
  } catch {
    /* ignore */
  }
  return SALES_REP_ROSTER[0].name;
}
